/**
 * Assets — production-design references for props, vehicles, set pieces,
 * wardrobe, anything the film needs. (USER YAP 2026-06-26 + _GLOBAL.md.)
 *
 * The locked shape, post-yap:
 *   - NO type. An asset is just a NAME + a (very physical) DESCRIPTION.
 *     The old Props / Vehicles / Set-pieces grouping is gone; assets
 *     render in one flat product-shot grid.
 *   - CREATE FROM A REFERENCE IMAGE. The add-asset flow LEADS with a
 *     reference-image upload (drop your own photo first), then name +
 *     description. The reference seeds every generated view.
 *   - EXTREMELY PHYSICAL descriptions (materials, dimensions, colour,
 *     finish, scale). The form nudges toward physical detail with a live
 *     "physical-detail" meter, and the salient physical attributes are
 *     HIGHLIGHTED inline (`<mark>`) on the card — same spirit as the
 *     Character panel's explicit-attribute tagging.
 *   - 360° TURNTABLE is the HERO / main view. front / three-quarter /
 *     left / back are SECONDARY source thumbnails along the bottom; the
 *     360° view is GENERATED FROM those source views.
 *   - NO "appears in scenes" chips. Usage now comes from `@`-references in
 *     the SCRIPT (_GLOBAL.md G1), not per-card scene lists.
 *   - EVERY image (reference, each source view) gets an Edit button that
 *     opens the ONE shared Photoshop-style image editor (G7).
 *   - The asset EDIT FORM is componentised (`buildAssetEditForm`) so the
 *     Script Editor can render it inside the shared entity drawer (G6);
 *     this panel's own Edit button opens that same form in the drawer.
 *
 * The reference image persists to `StudioAsset.referenceImage` (data URL).
 * Source-view images + turntable readiness are PANEL-LOCAL (the store has
 * no field for them yet) — see the final-message integrator note.
 */

import { BasePanel } from "./BasePanel";
import {
  buildEmptyState,
  buildHelperParagraph,
  buildLabeledField,
  buildPixelBox,
  buildSectionLabel,
  buildStudioButton,
} from "./dom";
import type { StudioAsset, StudioStore } from "../StudioStore";
import {
  closeEntityDrawer,
  openEntityDrawer,
  openImageEditor,
  placeholderImage,
  runGeneration,
} from "../shared";
import {
  ASSET_VIEW_ANGLES,
  buildHighlightedDescription,
  collectPhysicalAttributes,
  type AssetViewAngle,
  type AssetViewAngleDef,
} from "./assetPhysical";
import "../styles/assets.css";

interface AssetsPanelOptions {
  store: StudioStore;
}

/** Number of frames the turntable cycles through across a full drag. */
const TURNTABLE_FRAMES = 24;

/** Per-asset state this panel owns locally (not in StudioStore). */
interface LocalAssetState {
  /** Generated/uploaded source-view images, keyed by angle (data URL). */
  sourceViews: Record<AssetViewAngle, string | null>;
  /** Angles whose generation is currently in flight. */
  loadingViews: Set<AssetViewAngle>;
  /** Whether the 360° turntable has been generated from the source views. */
  turntableReady: boolean;
  /** True while the stubbed turntable generation is in flight. */
  turntableLoading: boolean;
  /** Current turntable frame index (0..TURNTABLE_FRAMES-1). */
  turntableFrame: number;
  /** Cached turntable frame images (data URLs), built on generate. */
  turntableImages: string[];
}

function emptySourceViews(): Record<AssetViewAngle, string | null> {
  const views = {} as Record<AssetViewAngle, string | null>;
  for (const angle of ASSET_VIEW_ANGLES) views[angle.key] = null;
  return views;
}

/** Read a File into a data URL (persistable; matches the image editor). */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export class AssetsPanel extends BasePanel {
  private readonly store: StudioStore;

  private nameInputEl: HTMLInputElement | null = null;
  private descriptionInputEl: HTMLTextAreaElement | null = null;
  private listEl: HTMLDivElement | null = null;
  private storeUnsubscribers: (() => void)[] = [];
  /** See CharacterCreatorPanel — diff for `.is-entering` animation. */
  private previouslyRenderedIds = new Set<string>();
  private hasRenderedOnce = false;
  /** Panel-local per-asset state keyed by asset id. */
  private readonly localState = new Map<string, LocalAssetState>();
  /** Pending reference data URL for the create-from-reference flow. */
  private pendingReferenceDataUrl: string | null = null;
  /** The create-form reference preview slot, refreshed on upload. */
  private referencePreviewEl: HTMLDivElement | null = null;
  /** The create-form physical-detail meter, refreshed as you type. */
  private detailMeterEl: HTMLDivElement | null = null;

  constructor(options: AssetsPanelOptions) {
    super();
    this.store = options.store;
  }

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");

    // ---- Add-new form (reference-FIRST) ----
    const formBox = buildPixelBox();
    formBox.fill.appendChild(
      buildHelperParagraph(
        "Add a prop, vehicle, set piece, or wardrobe item. Start from your own reference photo, then describe it physically — materials, dimensions, colour, finish, scale. Each asset becomes a product shot with a 360° turntable.",
      ),
    );

    // STEP 1 — reference image upload, at the START of the flow.
    formBox.fill.appendChild(this.buildCreateReferenceField());

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.className = "studio-input";
    nameInput.placeholder = "e.g. Super 8 Camera";
    nameInput.dataset.kokoAction = "asset-name";
    this.nameInputEl = nameInput;

    const descriptionInput = document.createElement("textarea");
    descriptionInput.className = "studio-textarea";
    descriptionInput.placeholder =
      "Be extremely physical: brass body, 12 cm long, scratched chrome lens ring, cracked leather strap, matte black grip.";
    descriptionInput.dataset.kokoAction = "asset-description";
    descriptionInput.addEventListener("input", () => this.refreshDetailMeter());
    this.descriptionInputEl = descriptionInput;

    formBox.fill.appendChild(buildLabeledField("Name", nameInput));
    formBox.fill.appendChild(
      buildLabeledField("Physical description", descriptionInput),
    );

    // Live physical-detail meter (encourages EXTREMELY physical copy).
    const meter = document.createElement("div");
    meter.className = "studio-asset-detail-meter";
    meter.dataset.kokoAction = "asset-detail-meter";
    this.detailMeterEl = meter;
    formBox.fill.appendChild(meter);
    this.refreshDetailMeter();

    const submitButton = buildStudioButton("Add asset", {
      variant: "primary",
      onClick: () => this.handleAddAsset(),
    });
    submitButton.dataset.kokoAction = "asset-submit";
    const buttonRow = document.createElement("div");
    buttonRow.className = "studio-button-row";
    buttonRow.appendChild(submitButton);
    formBox.fill.appendChild(buttonRow);

    root.appendChild(formBox.outer);

    // ---- Existing assets (flat product-shot grid) ----
    const listBox = buildPixelBox();
    listBox.fill.appendChild(buildSectionLabel("Inventory"));

    const list = document.createElement("div");
    list.className = "studio-assets-inventory";
    listBox.fill.appendChild(list);
    this.listEl = list;

    root.appendChild(listBox.outer);

    return root;
  }

  protected onMounted(): void {
    this.storeUnsubscribers.push(
      this.store.subscribe("assets", () => this.renderList()),
    );
    this.renderList();
  }

  protected onShown(): void {
    this.renderList();
    if (this.nameInputEl) this.nameInputEl.focus();
  }

  protected onBeforeDestroy(): void {
    for (const unsub of this.storeUnsubscribers) unsub();
    this.storeUnsubscribers = [];
    this.localState.clear();
  }

  // ------------------------------------------------------------------
  // Create-from-reference flow (reference upload FIRST)
  // ------------------------------------------------------------------

  private buildCreateReferenceField(): HTMLElement {
    const field = document.createElement("div");
    field.className = "studio-field studio-asset-create-ref";

    const label = document.createElement("label");
    label.className = "studio-field-label";
    label.textContent = "Reference photo (start here)";
    field.appendChild(label);

    const dropzone = document.createElement("label");
    dropzone.className = "studio-asset-dropzone";
    dropzone.dataset.kokoAction = "asset-create-reference";

    const preview = document.createElement("div");
    preview.className = "studio-asset-dropzone-preview";
    this.referencePreviewEl = preview;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.className = "studio-asset-dropzone-input";
    input.dataset.kokoAction = "asset-create-reference-input";
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (!file) return;
      void fileToDataUrl(file).then((dataUrl) => {
        this.pendingReferenceDataUrl = dataUrl;
        this.refreshReferencePreview();
      });
    });

    dropzone.appendChild(input);
    dropzone.appendChild(preview);
    field.appendChild(dropzone);

    const clear = buildStudioButton("Remove reference", {
      onClick: () => {
        this.pendingReferenceDataUrl = null;
        this.refreshReferencePreview();
      },
    });
    clear.dataset.kokoAction = "asset-create-reference-clear";
    clear.classList.add("studio-asset-create-ref-clear");
    field.appendChild(clear);

    this.refreshReferencePreview();
    return field;
  }

  private refreshReferencePreview(): void {
    const preview = this.referencePreviewEl;
    if (!preview) return;
    preview.replaceChildren();
    const field = preview.closest(".studio-asset-create-ref");
    if (this.pendingReferenceDataUrl) {
      const img = document.createElement("img");
      img.className = "studio-asset-dropzone-img";
      img.src = this.pendingReferenceDataUrl;
      img.alt = "Asset reference";
      preview.appendChild(img);
      field?.classList.add("has-image");
    } else {
      const hint = document.createElement("span");
      hint.className = "studio-asset-dropzone-hint";
      hint.textContent = "Drop or choose a reference photo";
      preview.appendChild(hint);
      field?.classList.remove("has-image");
    }
  }

  /** Update the live physical-detail meter from the description input. */
  private refreshDetailMeter(): void {
    const meter = this.detailMeterEl;
    if (!meter) return;
    const text = this.descriptionInputEl?.value ?? "";
    const attrs = collectPhysicalAttributes(text);
    meter.replaceChildren();

    const dot = document.createElement("span");
    dot.className = "studio-asset-detail-dot";
    const score = attrs.length;
    const level = score >= 4 ? "strong" : score >= 2 ? "ok" : "weak";
    meter.dataset.level = level;
    dot.dataset.level = level;

    const label = document.createElement("span");
    label.className = "studio-asset-detail-label";
    if (score === 0) {
      label.textContent =
        "Add physical detail — materials, dimensions, colour, finish, scale.";
    } else {
      label.textContent =
        level === "strong"
          ? `Nicely physical — ${score} attributes detected.`
          : `${score} physical attribute${score === 1 ? "" : "s"} detected — keep going.`;
    }

    meter.appendChild(dot);
    meter.appendChild(label);

    if (attrs.length > 0) {
      const chips = document.createElement("div");
      chips.className = "studio-asset-detail-chips";
      for (const attr of attrs.slice(0, 6)) {
        const chip = document.createElement("span");
        chip.className = "studio-asset-detail-chip";
        chip.dataset.kind = attr.kind;
        chip.textContent = attr.text;
        chips.appendChild(chip);
      }
      meter.appendChild(chips);
    }
  }

  private handleAddAsset(): void {
    if (!this.nameInputEl || !this.descriptionInputEl) return;
    const name = this.nameInputEl.value.trim();
    const description = this.descriptionInputEl.value.trim();
    if (name.length === 0 || description.length === 0) return;

    // NO type — assets are name + description only (YAP). The store's
    // legacy `type` param stays optional; pass undefined.
    const created = this.store.addAsset(name, undefined, description);
    if (this.pendingReferenceDataUrl) {
      this.store.updateAsset(created.id, {
        referenceImage: this.pendingReferenceDataUrl,
      });
    }

    this.nameInputEl.value = "";
    this.descriptionInputEl.value = "";
    this.pendingReferenceDataUrl = null;
    this.refreshReferencePreview();
    this.refreshDetailMeter();
    this.nameInputEl.focus();
  }

  // ------------------------------------------------------------------
  // Local per-asset state
  // ------------------------------------------------------------------

  private getLocal(assetId: string): LocalAssetState {
    let local = this.localState.get(assetId);
    if (!local) {
      local = {
        sourceViews: emptySourceViews(),
        loadingViews: new Set(),
        turntableReady: false,
        turntableLoading: false,
        turntableFrame: 0,
        turntableImages: [],
      };
      this.localState.set(assetId, local);
    }
    return local;
  }

  private hasAnySourceView(local: LocalAssetState): boolean {
    return ASSET_VIEW_ANGLES.some((a) => local.sourceViews[a.key] !== null);
  }

  // ------------------------------------------------------------------
  // Rendering
  // ------------------------------------------------------------------

  private renderList(): void {
    if (!this.listEl) return;
    this.listEl.replaceChildren();
    const assets = this.store.get("assets");
    if (assets.length === 0) {
      this.listEl.appendChild(
        buildEmptyState(
          "No assets yet. Drop a reference photo above and describe a prop, vehicle, set piece, or wardrobe item — or ask Koko to pull them from your script.",
        ),
      );
      this.previouslyRenderedIds = new Set();
      this.pruneLocalState(assets);
      return;
    }

    const grid = document.createElement("div");
    grid.className = "studio-card-grid studio-assets-grid";

    const nextRenderedIds = new Set<string>();
    let staggerIndex = 0;
    for (const asset of assets) {
      const isNew =
        this.hasRenderedOnce && !this.previouslyRenderedIds.has(asset.id);
      const cardEl = this.buildAssetCard(asset, isNew);
      if (isNew) cardEl.style.setProperty("--stagger", String(staggerIndex++));
      grid.appendChild(cardEl);
      nextRenderedIds.add(asset.id);
    }
    this.listEl.appendChild(grid);

    this.previouslyRenderedIds = nextRenderedIds;
    this.hasRenderedOnce = true;
    this.pruneLocalState(assets);
  }

  /** Drop local state for deleted assets. */
  private pruneLocalState(assets: StudioAsset[]): void {
    const liveIds = new Set(assets.map((asset) => asset.id));
    for (const id of [...this.localState.keys()]) {
      if (!liveIds.has(id)) this.localState.delete(id);
    }
  }

  private buildAssetCard(asset: StudioAsset, animateIn: boolean): HTMLElement {
    const card = buildPixelBox({ slim: true });
    card.fill.classList.add("studio-card", "studio-asset-card");
    card.fill.dataset.cardId = asset.id;
    if (animateIn) card.fill.classList.add("is-entering");

    // Rebuild fresh on every local-state change so the rebuilt card keeps
    // working affordances (avoids stale-closure bugs); see Character panel.
    const rerender = (): void => {
      card.fill.replaceChildren(this.buildAssetCardInner(asset, rerender));
    };
    rerender();
    return card.outer;
  }

  private buildAssetCardInner(
    asset: StudioAsset,
    rerender: () => void,
  ): HTMLElement {
    const local = this.getLocal(asset.id);
    const wrapper = document.createElement("div");
    wrapper.className = "studio-card-inner studio-asset-card-inner";

    // ---- HERO: 360° turntable (main view) ----
    wrapper.appendChild(this.buildTurntable(asset, local, rerender));

    // ---- SECONDARY: source-angle thumbnail strip ----
    wrapper.appendChild(this.buildSourceViewStrip(asset, local, rerender));

    // ---- Title + highlighted physical description ----
    const title = document.createElement("div");
    title.className = "studio-card-title";
    title.textContent = asset.name;
    wrapper.appendChild(title);

    if (asset.referenceImage) {
      wrapper.appendChild(this.buildReferenceRow(asset, rerender));
    }

    const body = document.createElement("div");
    body.className = "studio-card-body studio-asset-description";
    body.appendChild(buildHighlightedDescription(asset.description));
    wrapper.appendChild(body);

    // ---- Actions ----
    const actions = document.createElement("div");
    actions.className = "studio-card-actions";

    const editButton = buildStudioButton("Edit", {
      onClick: () => this.openEditDrawer(asset),
    });
    editButton.dataset.kokoAction = "asset-edit";
    editButton.dataset.id = asset.id;

    const deleteButton = buildStudioButton("Delete", {
      variant: "danger",
      onClick: () => this.store.deleteAsset(asset.id),
    });
    deleteButton.dataset.kokoAction = "asset-delete";
    deleteButton.dataset.id = asset.id;

    actions.appendChild(editButton);
    actions.appendChild(deleteButton);
    wrapper.appendChild(actions);

    return wrapper;
  }

  /** Compact reference-photo row with its own image-editor Edit (G7). */
  private buildReferenceRow(
    asset: StudioAsset,
    rerender: () => void,
  ): HTMLElement {
    const row = document.createElement("div");
    row.className = "studio-asset-reference-row";

    const thumb = document.createElement("div");
    thumb.className = "studio-asset-reference-thumb";
    const img = document.createElement("img");
    img.src = asset.referenceImage ?? "";
    img.alt = `${asset.name} reference`;
    thumb.appendChild(img);
    row.appendChild(thumb);

    const meta = document.createElement("div");
    meta.className = "studio-asset-reference-meta";
    const label = document.createElement("div");
    label.className = "studio-asset-reference-label";
    label.textContent = "Created from reference";
    meta.appendChild(label);

    const editButton = buildStudioButton("Edit image", {
      onClick: () =>
        openImageEditor({
          image: asset.referenceImage ?? "",
          title: `Edit reference — ${asset.name}`,
          onChange: (next) =>
            this.store.updateAsset(asset.id, { referenceImage: next }),
          onDone: () => rerender(),
        }),
    });
    editButton.dataset.kokoAction = "asset-image-edit";
    editButton.dataset.target = "reference";
    editButton.dataset.id = asset.id;
    meta.appendChild(editButton);

    row.appendChild(meta);
    return row;
  }

  /**
   * 360° turntable HERO. States: empty CTA (generate from source views) →
   * loading spinner → drag-to-spin viewer cycling generated frames.
   */
  private buildTurntable(
    asset: StudioAsset,
    local: LocalAssetState,
    rerender: () => void,
  ): HTMLElement {
    const slot = document.createElement("div");
    slot.className = "studio-turntable";
    slot.dataset.kokoAction = "asset-turntable";
    slot.dataset.id = asset.id;

    if (local.turntableLoading) {
      slot.classList.add("is-loading");
      const spinner = document.createElement("div");
      spinner.className = "studio-turntable-spinner";
      const caption = document.createElement("div");
      caption.className = "studio-turntable-caption";
      caption.textContent = "Building 360° turntable from source views…";
      slot.appendChild(spinner);
      slot.appendChild(caption);
      return slot;
    }

    if (!local.turntableReady) {
      slot.classList.add("is-empty");
      const hint = document.createElement("div");
      hint.className = "studio-turntable-hint";
      hint.textContent = "360° turntable";
      const sub = document.createElement("div");
      sub.className = "studio-turntable-subhint";
      const ready = this.hasAnySourceView(local);
      sub.textContent = ready
        ? "Generate a spinnable hero view from the source angles below."
        : "Add at least one source angle below, then generate the 360° view.";
      const generateButton = buildStudioButton("Generate 360° turntable", {
        variant: "primary",
        onClick: () => this.handleGenerateTurntable(asset, local, rerender),
      });
      generateButton.dataset.kokoAction = "asset-turntable-generate";
      generateButton.dataset.id = asset.id;
      generateButton.disabled = !ready;
      slot.appendChild(hint);
      slot.appendChild(sub);
      slot.appendChild(generateButton);
      return slot;
    }

    // Ready: drag-to-spin viewer over generated frames.
    slot.classList.add("is-ready");
    const stage = document.createElement("div");
    stage.className = "studio-turntable-stage";

    const frame = document.createElement("div");
    frame.className = "studio-turntable-frame";
    const frameImg = document.createElement("img");
    frameImg.className = "studio-turntable-frame-img";
    frameImg.alt = `${asset.name} — 360° view`;
    frame.appendChild(frameImg);

    const applyFrame = () => {
      const angle = Math.round((local.turntableFrame / TURNTABLE_FRAMES) * 360);
      frame.dataset.frame = String(local.turntableFrame);
      frame.dataset.angle = String(angle);
      const img = local.turntableImages[local.turntableFrame];
      if (img) frameImg.src = img;
    };
    applyFrame();
    stage.appendChild(frame);

    const badge = document.createElement("span");
    badge.className = "studio-turntable-anglebadge";
    const updateBadge = () => {
      const angle = Math.round((local.turntableFrame / TURNTABLE_FRAMES) * 360);
      badge.textContent = `${angle}°`;
    };
    updateBadge();
    stage.appendChild(badge);

    const caption = document.createElement("div");
    caption.className = "studio-turntable-caption";
    caption.textContent = "drag to spin · generated from source views";

    // Pointer drag → frame index (full stage width = 360°).
    let dragging = false;
    let startX = 0;
    let startFrame = 0;
    const onDown = (event: PointerEvent) => {
      dragging = true;
      startX = event.clientX;
      startFrame = local.turntableFrame;
      stage.classList.add("is-dragging");
      stage.setPointerCapture(event.pointerId);
    };
    const onMove = (event: PointerEvent) => {
      if (!dragging) return;
      const width = stage.clientWidth || 1;
      const deltaFrames = Math.round(
        ((event.clientX - startX) / width) * TURNTABLE_FRAMES,
      );
      const next =
        (((startFrame + deltaFrames) % TURNTABLE_FRAMES) + TURNTABLE_FRAMES) %
        TURNTABLE_FRAMES;
      if (next !== local.turntableFrame) {
        local.turntableFrame = next;
        applyFrame();
        updateBadge();
      }
    };
    const onUp = (event: PointerEvent) => {
      dragging = false;
      stage.classList.remove("is-dragging");
      try {
        stage.releasePointerCapture(event.pointerId);
      } catch {
        // Capture may already be released — ignore.
      }
    };
    stage.addEventListener("pointerdown", onDown);
    stage.addEventListener("pointermove", onMove);
    stage.addEventListener("pointerup", onUp);
    stage.addEventListener("pointercancel", onUp);

    slot.appendChild(stage);
    slot.appendChild(caption);
    return slot;
  }

  private handleGenerateTurntable(
    asset: StudioAsset,
    local: LocalAssetState,
    rerender: () => void,
  ): void {
    if (local.turntableLoading || local.turntableReady) return;
    if (!this.hasAnySourceView(local)) return;
    local.turntableLoading = true;
    rerender();
    runGeneration({
      kind: "image",
      prompt: `360 turntable of ${asset.name}: ${asset.description}`,
      aspect: "1:1",
      seed: `${asset.id}-turntable`,
    })
      .then(() => {
        // Build the spin frames as deterministic placeholders seeded by
        // angle (stand-ins for real renders derived from the source views).
        local.turntableImages = Array.from(
          { length: TURNTABLE_FRAMES },
          (_, i) => {
            const angle = Math.round((i / TURNTABLE_FRAMES) * 360);
            return placeholderImage(
              `${asset.id}-turn-${i}`,
              [],
              `${angle}°`,
              "1:1",
            );
          },
        );
        local.turntableLoading = false;
        local.turntableReady = true;
        local.turntableFrame = 0;
        rerender();
      })
      .catch(() => {
        local.turntableLoading = false;
        rerender();
      });
  }

  /**
   * Secondary SOURCE-VIEW strip: front / three-quarter / left / back.
   * Each tile generates (or, once ready, edits via the shared editor) the
   * angle the 360° turntable is built from.
   */
  private buildSourceViewStrip(
    asset: StudioAsset,
    local: LocalAssetState,
    rerender: () => void,
  ): HTMLElement {
    const block = document.createElement("div");
    block.className = "studio-asset-views";
    block.dataset.kokoAction = "asset-views";
    block.dataset.id = asset.id;

    const label = document.createElement("div");
    label.className = "studio-asset-views-label";
    label.textContent = "Source views — the 360° is built from these";
    block.appendChild(label);

    const strip = document.createElement("div");
    strip.className = "studio-asset-views-strip";
    for (const angle of ASSET_VIEW_ANGLES) {
      strip.appendChild(this.buildSourceViewTile(asset, local, angle, rerender));
    }
    block.appendChild(strip);
    return block;
  }

  private buildSourceViewTile(
    asset: StudioAsset,
    local: LocalAssetState,
    angle: AssetViewAngleDef,
    rerender: () => void,
  ): HTMLElement {
    const tile = document.createElement("div");
    tile.className = "studio-asset-view";
    tile.dataset.angle = angle.key;

    const image = local.sourceViews[angle.key];
    const isLoading = local.loadingViews.has(angle.key);

    const frame = document.createElement("div");
    frame.className = "studio-asset-view-frame";

    if (isLoading) {
      tile.classList.add("is-loading");
      const spin = document.createElement("div");
      spin.className = "studio-asset-view-spinner";
      frame.appendChild(spin);
    } else if (image) {
      tile.classList.add("has-image");
      const img = document.createElement("img");
      img.className = "studio-asset-view-img";
      img.src = image;
      img.alt = `${asset.name} — ${angle.label}`;
      frame.appendChild(img);

      // G7: every image gets an Edit button → shared image editor.
      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "studio-asset-view-edit";
      edit.textContent = "Edit";
      edit.title = `Edit ${angle.label} view`;
      edit.dataset.kokoAction = "asset-image-edit";
      edit.dataset.target = angle.key;
      edit.dataset.id = asset.id;
      edit.addEventListener("click", () =>
        openImageEditor({
          image,
          title: `Edit ${angle.label} — ${asset.name}`,
          onChange: (next) => {
            local.sourceViews[angle.key] = next;
            // A source-view edit invalidates the derived turntable.
            local.turntableReady = false;
            local.turntableImages = [];
          },
          onDone: () => rerender(),
        }),
      );
      frame.appendChild(edit);
    } else {
      tile.classList.add("is-empty");
      const add = document.createElement("button");
      add.type = "button";
      add.className = "studio-asset-view-generate";
      add.dataset.kokoAction = "asset-view-generate";
      add.dataset.angle = angle.key;
      add.dataset.id = asset.id;
      add.textContent = "+";
      add.title = `Generate ${angle.label} view`;
      add.addEventListener("click", () =>
        this.handleGenerateView(asset, local, angle.key, rerender),
      );
      frame.appendChild(add);
    }

    const cap = document.createElement("div");
    cap.className = "studio-asset-view-caption";
    cap.textContent = angle.label;

    tile.appendChild(frame);
    tile.appendChild(cap);
    return tile;
  }

  private handleGenerateView(
    asset: StudioAsset,
    local: LocalAssetState,
    key: AssetViewAngle,
    rerender: () => void,
  ): void {
    if (local.loadingViews.has(key) || local.sourceViews[key]) return;
    local.loadingViews.add(key);
    rerender();
    runGeneration({
      kind: "image",
      prompt: `${key} product view of ${asset.name}: ${asset.description}`,
      aspect: "1:1",
      seed: `${asset.id}-${key}`,
    })
      .then((result) => {
        local.sourceViews[key] =
          result.url ?? placeholderImage(`${asset.id}-${key}`, [], key, "1:1");
        local.loadingViews.delete(key);
        // A new source view invalidates any previously built turntable.
        local.turntableReady = false;
        local.turntableImages = [];
        rerender();
      })
      .catch(() => {
        local.loadingViews.delete(key);
        rerender();
      });
  }

  // ------------------------------------------------------------------
  // Edit (componentised form → shared entity drawer, G6)
  // ------------------------------------------------------------------

  /** Open the asset edit form inside the shared entity drawer (G6). */
  private openEditDrawer(asset: StudioAsset): void {
    openEntityDrawer({
      title: `Edit ${asset.name}`,
      subtitle: asset.id,
      render: (body) =>
        body.appendChild(
          buildAssetEditForm(this.store, asset, {
            onSaved: () => closeEntityDrawer(),
            onCancel: () => closeEntityDrawer(),
          }),
        ),
    });
  }
}

// --------------------------------------------------------------------
// Componentised edit form (G6) — exported so the Script Editor can render
// it inside the shared entity drawer when an asset is clicked in-script.
// --------------------------------------------------------------------

export interface AssetEditFormOptions {
  onSaved?: (asset: StudioAsset) => void;
  onCancel?: () => void;
}

/**
 * Build the asset EDIT FORM (name + physical description, NO type).
 * Mirrors the Character panel's explicit-attribute spirit by surfacing a
 * live highlight preview of the physical attributes in the description.
 * Reused by AssetsPanel's Edit drawer and by the Script Editor's in-script
 * click→drawer (G6).
 */
export function buildAssetEditForm(
  store: StudioStore,
  asset: StudioAsset,
  options: AssetEditFormOptions = {},
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "studio-card-inner studio-asset-edit-form";

  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.className = "studio-input";
  nameInput.value = asset.name;
  nameInput.dataset.kokoAction = "asset-edit-name";
  nameInput.dataset.id = asset.id;

  const descInput = document.createElement("textarea");
  descInput.className = "studio-textarea";
  descInput.value = asset.description;
  descInput.dataset.kokoAction = "asset-edit-description";
  descInput.dataset.id = asset.id;

  // Live highlight preview of detected physical attributes.
  const preview = document.createElement("div");
  preview.className = "studio-asset-edit-preview";
  preview.dataset.kokoAction = "asset-edit-preview";
  const refreshPreview = () => {
    preview.replaceChildren();
    const attrs = collectPhysicalAttributes(descInput.value);
    if (attrs.length === 0) {
      const hint = document.createElement("span");
      hint.className = "studio-asset-edit-preview-hint";
      hint.textContent =
        "No physical attributes detected yet — add materials, dimensions, colour, finish.";
      preview.appendChild(hint);
      return;
    }
    for (const attr of attrs) {
      const chip = document.createElement("span");
      chip.className = "studio-asset-detail-chip";
      chip.dataset.kind = attr.kind;
      chip.textContent = attr.text;
      preview.appendChild(chip);
    }
  };
  descInput.addEventListener("input", refreshPreview);
  refreshPreview();

  const actions = document.createElement("div");
  actions.className = "studio-card-actions";

  const saveButton = buildStudioButton("Save", {
    variant: "primary",
    onClick: () => {
      const name = nameInput.value.trim();
      const description = descInput.value.trim();
      if (name.length === 0 || description.length === 0) return;
      store.updateAsset(asset.id, { name, description });
      options.onSaved?.({ ...asset, name, description });
    },
  });
  saveButton.dataset.kokoAction = "asset-edit-save";
  saveButton.dataset.id = asset.id;

  const cancelButton = buildStudioButton("Cancel", {
    onClick: () => options.onCancel?.(),
  });
  cancelButton.dataset.kokoAction = "asset-edit-cancel";
  cancelButton.dataset.id = asset.id;

  actions.appendChild(saveButton);
  actions.appendChild(cancelButton);

  wrapper.appendChild(buildLabeledField("Name", nameInput));
  wrapper.appendChild(buildLabeledField("Physical description", descInput));
  wrapper.appendChild(preview);
  wrapper.appendChild(actions);
  return wrapper;
}
